import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IPrinterConfig } from '../../models';
import { IPrinterConnectionStrategy } from './printer-connection-strategy.interface';

@Injectable({
  providedIn: 'root',
})
export class TcpConnectionStrategy implements IPrinterConnectionStrategy {
  private readonly translateService = inject(TranslateService);
  private readonly tcpConnections = new Map<string, { ip: string; port: number }>();

  async connect(printer: IPrinterConfig): Promise<boolean> {
    if (!printer.ipAddress) {
      throw new Error(this.translateService.instant('printers.errors.ip_address_missing'));
    }

    // For Manual IP printers, we typically use one of these approaches:
    // 1. Raw TCP socket (requires Direct Sockets API - not available in standard web)
    // 2. HTTP POST to printer's web interface (if supported)
    // 3. WebSocket proxy server (requires backend support)

    // For now, we'll mark as connected and use HTTP POST when printing
    // Most modern network printers support HTTP printing at port 631 (IPP) or 9100

    const port = printer.port || 9100;
    const url = `http://${printer.ipAddress}:${port}`;

    try {
      // Test connectivity with a simple fetch (may fail due to CORS in browser)
      // In production, this would go through a backend proxy
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      try {
        // Try to reach the printer - this is a connectivity test
        // Note: This will likely fail due to CORS, but we catch and handle it
        await fetch(url, {
          method: 'HEAD',
          mode: 'no-cors',
          signal: controller.signal,
        });
      } catch (error) {
        // If fetch throws, it means network error (timeout, connection refused, or CSP blocked)
        // In all these cases, we cannot print, so we should treat it as failure.
        console.error(`Failed to connect to printer at ${url}:`, error);
        throw new Error(this.translateService.instant('printers.errors.unable_to_reach_printer', { ip: printer.ipAddress, port }));
      } finally {
        clearTimeout(timeoutId);
      }

      // Store connection info
      this.tcpConnections.set(printer.id, { ip: printer.ipAddress, port });
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(this.translateService.instant('printers.errors.connection_timeout', { ip: printer.ipAddress, port }));
      }
      throw error;
    }
  }

  async disconnect(printerId: string): Promise<void> {
    this.tcpConnections.delete(printerId);
  }

  async sendData(printerId: string, data: Uint8Array): Promise<boolean> {
    const connectionInfo = this.tcpConnections.get(printerId);

    if (!connectionInfo) {
      throw new Error(this.translateService.instant('printers.errors.no_tcp_info'));
    }

    // In a real implementation, this would:
    // 1. Send data through a WebSocket proxy to the backend
    // 2. Backend would forward to the printer via raw TCP
    // For now, we'll try HTTP POST (works with some printers)

    const url = `http://${connectionInfo.ip}:${connectionInfo.port}`;

    try {
      // Attempt to send via HTTP POST
      // Note: This requires the printer to support HTTP or a backend proxy
      // Create a proper ArrayBuffer from Uint8Array
      const buffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
      await fetch(url, {
        method: 'POST',
        mode: 'no-cors', // Needed for cross-origin requests to printers
        body: buffer,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      });

      // no-cors mode doesn't give us response status
      // We assume success if no network error thrown
      return true;
    } catch (error) {
      console.error('TCP send error:', error);
      throw new Error(this.translateService.instant('printers.errors.tcp_send_failed'));
    }
  }

  hasConnection(printerId: string): boolean {
    return this.tcpConnections.has(printerId);
  }
}
